import { PositionTabsElementType, ViewTabsElementType } from '@vedalib/editor/lib/brand'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { useUpdateEffect } from 'ahooks'
import lodash from 'lodash'
import { useCallback, useMemo, useRef, useState } from 'react'

import Node from 'components/Editor/cource/layout/Node'
import { DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import { useElementCompleted } from '../../hooks/useElementCompleted'
import useSetActiveItemElement from '../../hooks/useSetActiveItemElement'
import IconBottomDefaultSVG from './assets/nav_icon_bottom.svg?react'
import IconLeftDefaultSVG from './assets/nav_icon_left.svg?react'
import IconRightDefaultSVG from './assets/nav_icon_right.svg?react'
import IconTopDefaultSVG from './assets/nav_icon_top.svg?react'
import { useTabScroll } from './hooks'
import NavButtonContainerStyled from './styled/NavButtonContainerStyled'
import NavButtonStyled from './styled/NavButtonStyled'
import TabElementStyled from './styled/TabElementStyled'
import TabItemMarkerStyled from './styled/TabItemMarkerStyled'
import TabItemStyled from './styled/TabItemStyled'
import TabItemTextStyled from './styled/TabItemTextStyled'
import TabItemsListStyled from './styled/TabItemsListStyled'
import TabItemsScrollStyled, { OVERFLOW_OFFSET } from './styled/TabItemsScrollStyled'
import TabItemsStyled from './styled/TabItemsStyled'
import TabPanelStyled from './styled/TabPanelStyled'
import type { TabsElementType } from './tabsTypes'
import { DirectionTabsEnum } from './types'

const TabsElement: TabsElementType = ({
  block,
  font,
  element,
  styles,
  mode,
  state,
  isV2,
  setState,
}) => {
  const isDesktop = mode.deviceMode === DeviceMode.desktop
  const items = element.value.items
  const showButtons = styles.icons.show
  const iconLeft = styles.iconLeft.backgroundImage
  const iconRight = styles.iconRight.backgroundImage
  const notTopDirectionTabs =
    (styles.tabs.position as PositionTabsElementType) !== PositionTabsElementType.top
  const ImgDefaultPrev = notTopDirectionTabs ? IconTopDefaultSVG : IconLeftDefaultSVG
  const ImgDefaultNext = notTopDirectionTabs ? IconBottomDefaultSVG : IconRightDefaultSVG
  const scrollTabsRef = useRef<HTMLDivElement | null>(null)
  const activeTabRef = useRef<HTMLDivElement | null>(null)
  const listTabRef = useRef<HTMLUListElement | null>(null)
  const tabsRef = useRef<HTMLLIElement[]>([])
  const [viewedTabs, setViewedTabs] = useState(
    items.reduce<Record<string, boolean>>(
      (acc, item, i) => ({ ...acc, [item.value]: i === 0 }),
      {},
    ),
  )
  const allViewed = useMemo(() => lodash.every(viewedTabs), [viewedTabs])
  const activeTabId = useMemo(
    () => items.find(({ value }) => value === state?.active)?.value || items[0]?.value,
    [state?.active, items],
  )
  const { hasNextScroll, hasPrevScroll } = useTabScroll({
    scrollTabsRef,
    activeTabId,
    activeTabRef,
    listTabRef,
    deps: [items.length, mode.deviceMode],
  })

  useElementCompleted({
    block,
    elementId: element.id,
    completed: allViewed,
    deviceMode: mode.deviceMode,
  })
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.tabs.type,
      elementId: element.id,
    },
  )

  // TODO: check deps rebase
  const onChangeTab = useCallback(
    (value: string) => {
      setState?.({ active: value, pauseAnimation: false })
      const data = entityAnalyticsConfig.selected({
        resultExtensions: {
          tabLabel: items.find(({ value: v }) => v === value)?.label || '',
          tabIndex: items.findIndex(({ value: v }) => v === value),
          tabCount: items.length,
        },
        objectExtensions: { itemId: value, ...baseObjectExtensions },
        elementId: element.id,
      })
      sendXapiData(data)
      if (!allViewed) {
        setViewedTabs((prev) => ({ ...prev, [value]: true }))
      }
    },
    [
      setState,
      entityAnalyticsConfig,
      items,
      baseObjectExtensions,
      element.id,
      allViewed,
      sendXapiData,
    ],
  )

  const getTabId = (id: string) => {
    return `tab-${element.id}-${id}`
  }

  const getTabPanelId = (id: string) => {
    return `tabpanel-${element.id}-${id}`
  }

  const handleClick = useCallback(
    (value: string) => {
      onChangeTab(value)
    },
    [onChangeTab],
  )

  const onChangeNewTab = useCallback(
    (direction: DirectionTabsEnum) => {
      const index = items.findIndex(({ value }) => value === activeTabId)
      if (direction === DirectionTabsEnum.next && index < items.length - 1) {
        onChangeTab(items[index + 1].value)
        tabsRef.current[index + 1]?.focus()
        activeTabRef.current?.focus()
      }

      if (direction === DirectionTabsEnum.prev && index > 0) {
        onChangeTab(items[index - 1].value)
        tabsRef.current[index - 1]?.focus()
        activeTabRef.current?.focus()
      }
    },
    [items, activeTabId, onChangeTab],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>) => {
      if (
        (e.key === 'ArrowLeft' && !notTopDirectionTabs) ||
        (e.key === 'ArrowUp' && notTopDirectionTabs)
      ) {
        e.preventDefault()
        onChangeNewTab(DirectionTabsEnum.prev)
      }
      if (
        (e.key === 'ArrowRight' && !notTopDirectionTabs) ||
        (e.key === 'ArrowDown' && notTopDirectionTabs)
      ) {
        e.preventDefault()
        onChangeNewTab(DirectionTabsEnum.next)
      }
      if (e.key === 'Home') {
        e.preventDefault()
        onChangeTab(items[0].value)
      }
      if (e.key === 'End') {
        e.preventDefault()
        onChangeTab(items[items.length - 1].value)
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onChangeTab(activeTabId)
      }
    },
    [activeTabId, onChangeTab, items, notTopDirectionTabs, onChangeNewTab],
  )

  useSetActiveItemElement(items, block!, onChangeTab)

  useUpdateEffect(() => {
    setState?.({ active: state?.active || null, pauseAnimation: true })
  }, [styles.tabs.position, styles.view.type])

  if (mode.previewMode === PreviewMode.pdf) {
    return items.map((item) => (
      <TabElementStyled $styles={styles} key={item.value}>
        <TabItemsListStyled $styles={styles}>
          <TabItemsScrollStyled $styles={styles}>
            <TabItemsStyled $styles={styles}>
              <TabItemStyled $font={font} $styles={styles} $active>
                <TabItemMarkerStyled $font={font} $styles={styles} $active />
                <TabItemTextStyled $font={font} $styles={styles}>
                  {item.label}
                </TabItemTextStyled>
              </TabItemStyled>
            </TabItemsStyled>
          </TabItemsScrollStyled>
        </TabItemsListStyled>
        <TabPanelStyled $styles={styles}>
          <Node block={block!} id={item.value} mode={mode} isItem />
        </TabPanelStyled>
      </TabElementStyled>
    ))
  }

  return (
    <TabElementStyled $isV2={isV2} $styles={styles}>
      {/*need for bottom line, that overflow in scroll hide content  which not static */}
      <TabItemsListStyled $styles={styles}>
        {hasPrevScroll && showButtons && (
          <NavButtonContainerStyled
            $iconSide={DirectionTabsEnum.prev}
            $isDesktop={isDesktop}
            $styles={styles}
            onClick={() => onChangeNewTab(DirectionTabsEnum.prev)}
          >
            <NavButtonStyled $iconSide={DirectionTabsEnum.prev} $styles={styles}>
              {!iconLeft && <ImgDefaultPrev />}
            </NavButtonStyled>
          </NavButtonContainerStyled>
        )}
        <TabItemsScrollStyled
          $hasNextScroll={hasNextScroll}
          $hasPrevScroll={hasPrevScroll}
          $styles={styles}
          ref={scrollTabsRef}
        >
          <TabItemsStyled
            $styles={styles}
            aria-labelledby={getTabId(activeTabId)}
            aria-orientation={notTopDirectionTabs ? 'vertical' : 'horizontal'}
            ref={listTabRef}
            role='tablist'
          >
            {items.map((item) => (
              <TabItemStyled
                $active={activeTabId === item.value}
                $font={font}
                $styles={styles}
                aria-controls={getTabPanelId(item.value)}
                aria-selected={activeTabId === item.value}
                id={getTabId(item.value)}
                key={item.value}
                onClick={() => handleClick(item.value)}
                onKeyDown={handleKeyDown}
                ref={(el) => tabsRef.current.push(el as HTMLLIElement)}
                role='tab'
                tabIndex={activeTabId === item.value ? 0 : -1}
              >
                {activeTabId === item.value && (
                  <TabItemMarkerStyled
                    $active={activeTabId === item.value}
                    // $focus={focused}
                    $font={font}
                    $styles={styles}
                    initial={false}
                    layoutId={`bubble-group${element.id}`}
                    ref={activeTabRef}
                    transition={{ duration: state?.pauseAnimation ? 0 : 0.4 }}
                  />
                )}
                <TabItemTextStyled
                  $active={activeTabId === item.value}
                  $font={font}
                  $styles={styles}
                >
                  {item.label}
                </TabItemTextStyled>
              </TabItemStyled>
            ))}
            {styles.view.type === ViewTabsElementType.splitted &&
              (styles.tabs.position as PositionTabsElementType) === PositionTabsElementType.top && (
                <div style={{ minWidth: OVERFLOW_OFFSET - 8 }} />
              )}
          </TabItemsStyled>
        </TabItemsScrollStyled>
        {hasNextScroll && showButtons && (
          <NavButtonContainerStyled
            $iconSide={DirectionTabsEnum.next}
            $isDesktop={isDesktop}
            $styles={styles}
            onClick={() => onChangeNewTab(DirectionTabsEnum.next)}
          >
            <NavButtonStyled $iconSide={DirectionTabsEnum.next} $styles={styles}>
              {!iconRight && <ImgDefaultNext />}
            </NavButtonStyled>
          </NavButtonContainerStyled>
        )}
      </TabItemsListStyled>
      <TabPanelStyled
        $isV2={isV2}
        $styles={styles}
        aria-labelledby={getTabId(activeTabId)}
        id={getTabPanelId(activeTabId)}
        role='tabpanel'
        tabIndex={0}
      >
        <Node block={block!} id={activeTabId} mode={mode} isItem />
      </TabPanelStyled>
    </TabElementStyled>
  )
}

export default TabsElement
